import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const PROMO_VIDEO_URL = "https://businessesppall.nyc3.cdn.digitaloceanspaces.com/06082026.mp4";

interface PromoVideoDialogProps {
  open: boolean;
  onClose: () => void;
  onPlay?: () => void;
  onEnded?: () => void;
}

export default function PromoVideoDialog({ open, onClose, onPlay, onEnded }: PromoVideoDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, video[controls], a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      videoRef.current?.pause();
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="promo-dialog-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="promo-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="promo-dialog-title" aria-describedby="promo-dialog-description">
        <div className="promo-dialog-header">
          <div>
            <span className="promo-dialog-eyebrow">Ontoligent / Platform preview</span>
            <h2 id="promo-dialog-title">Watch the platform in action</h2>
            <p id="promo-dialog-description">From enterprise knowledge to a governed decision.</p>
          </div>
          <button ref={closeRef} className="promo-dialog-close" type="button" aria-label="Close video" onClick={onClose}><X size={20} aria-hidden /></button>
        </div>
        <video ref={videoRef} className="promo-dialog-video" src={PROMO_VIDEO_URL} controls autoPlay playsInline preload="metadata" tabIndex={0} onPlay={onPlay} onEnded={onEnded} />
        <div className="promo-dialog-footer">Knowledge <span>·</span> Context <span>·</span> Reasoning <span>·</span> Action</div>
      </div>
      <style>{`
        .promo-dialog-backdrop{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;padding:clamp(12px,3vw,40px);background:hsl(var(--bg)/.84);backdrop-filter:blur(14px)}
        .promo-dialog{width:min(100%,1050px);max-height:calc(100dvh - 24px);overflow:auto;border:1px solid hsl(var(--border));background:hsl(var(--panel));color:hsl(var(--text));box-shadow:0 32px 100px hsl(0 0% 0%/.4)}
        .promo-dialog-header{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding:clamp(18px,3vw,30px);border-bottom:1px solid hsl(var(--border))}
        .promo-dialog-eyebrow,.promo-dialog-footer{font:10px/1.5 'IBM Plex Mono',ui-monospace,monospace;letter-spacing:.13em;text-transform:uppercase;color:hsl(var(--accent))}
        .promo-dialog h2{margin:8px 0 4px;font:500 clamp(30px,4vw,52px)/1.02 'IBM Plex Sans',sans-serif;letter-spacing:-.035em}
        .promo-dialog p{margin:0;color:hsl(var(--muted));font-size:14px;line-height:1.5}
        .promo-dialog-close{flex:none;display:grid;place-items:center;width:40px;height:40px;border:1px solid hsl(var(--border));background:transparent;color:hsl(var(--text));cursor:pointer}
        .promo-dialog-close:hover{border-color:hsl(var(--accent));background:hsl(var(--accent)/.1)}
        .promo-dialog-close:focus-visible{outline:2px solid hsl(var(--accent));outline-offset:2px}
        .promo-dialog-video{display:block;width:100%;max-height:min(66dvh,620px);aspect-ratio:16/9;background:#080b0f;object-fit:contain}
        .promo-dialog-footer{padding:12px clamp(18px,3vw,30px);border-top:1px solid hsl(var(--border));color:hsl(var(--muted))}
        .promo-dialog-footer span{color:hsl(var(--accent));padding:0 .5em}
      `}</style>
    </div>,
    document.body
  );
}
